/**
 * Draft Content Renderer
 *
 * Renders draft section content with support for:
 * - HTML tables from DOCX parsing (content_html field)
 * - Comment highlighting (click to scroll)
 * - Search term highlighting
 * - Plain text fallback for legacy drafts
 *
 * Uses same DOMPurify configuration and Tailwind classes as SplContentRenderer.
 */
import React, { useMemo, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import type { CommentResponse } from '../types';
import { findTermRanges, highlightHtmlString } from './SplContentRenderer';

interface DraftContentRendererProps {
  /** HTML-formatted content (from content_html field) */
  contentHtml?: string | null | undefined;
  /** Plain text content (fallback) */
  contentText: string;
  /** Comments to highlight in the content */
  comments?: CommentResponse[];
  /** Currently selected comment ID (for active highlight) */
  selectedCommentId?: number | null;
  /** Callback when a comment highlight is clicked */
  onCommentClick?: (commentId: number) => void;
  /** Search terms to highlight */
  highlightTerms?: string[];
  englishExact?: boolean;
  /** Optional className */
  className?: string;
}

/**
 * Allowed HTML tags for DOMPurify sanitization
 * Matches SplContentRenderer for consistency
 */
const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li',
  'a',
  'sup', 'sub',
  'table', 'caption', 'thead', 'tfoot', 'tbody', 'tr', 'th', 'td', 'col', 'colgroup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'mark', // For highlighting
  'del', 'ins', // Track changes
];

const ALLOWED_ATTR = [
  'class', 'href', 'src', 'alt',
  'colspan', 'rowspan', 'width', 'valign', 'align',
  'data-comment-id', 'data-highlight', 'id', 'style',
  'target', 'rel', 'title',
  'data-author', 'data-date', // Track change metadata
];

/**
 * Highlight comment context text in HTML content
 * Injects data-comment-id attributes for click handling
 */
function highlightCommentsInHtml(
  html: string,
  comments: CommentResponse[],
  selectedCommentId: number | null
): string {
  if (!comments || comments.length === 0) return html;

  let result = html;

  // Sort comments by context_text length (longest first) to avoid partial matches
  const sortedComments = [...comments]
    .filter(c => c.context_text && c.context_text.trim())
    .sort((a, b) => (b.context_text?.length || 0) - (a.context_text?.length || 0));

  for (const comment of sortedComments) {
    const contextText = comment.context_text;
    if (!contextText) continue;

    // Escape special regex characters in the context text
    const escapedText = contextText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create regex to find the context text (case-insensitive)
    const regex = new RegExp(`(${escapedText})`, 'gi');

    // Check if this is the selected comment
    const isSelected = comment.id === selectedCommentId;
    const highlightClass = isSelected
      ? 'bg-amber-300 border-b-2 border-amber-500 cursor-pointer comment-highlight'
      : 'bg-amber-100 hover:bg-amber-200 cursor-pointer comment-highlight';

    // Replace with highlighted version (only first match gets id for scroll targeting)
    let firstMatch = true;
    result = result.replace(regex, (match) => {
      const idAttr = firstMatch ? ` id="comment-highlight-${comment.id}"` : '';
      firstMatch = false;
      return `<mark class="${highlightClass}" data-comment-id="${comment.id}"${idAttr}>${match}</mark>`;
    });
  }

  return result;
}

/**
 * Highlight search terms in HTML content
 * Carefully injects <mark> tags only in text nodes, preserving HTML structure
 */
function highlightSearchTermsInHtml(html: string, terms: string[], englishExact = false): string {
  if (!terms || terms.length === 0) return html;
  if (englishExact) {
    const div = document.createElement('div');
    div.innerHTML = highlightHtmlString(html, terms, undefined, null, { englishExact: true });
    div.querySelectorAll('mark.search-highlight').forEach((mark) => {
      mark.classList.remove('bg-amber-200');
      mark.classList.add('bg-cyan-200');
    });
    return div.innerHTML;
  }

  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Build regex for matching terms (case insensitive, whole words)
  const escapedTerms = terms.map(term =>
    term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  const regex = new RegExp(`\\b(${escapedTerms.join('|')})\\b`, 'gi');

  // Recursively process text nodes
  function processNode(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (regex.test(text)) {
        // Reset regex lastIndex after test
        regex.lastIndex = 0;

        // Create a document fragment with highlighted text
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
          // Add text before match
          if (match.index > lastIndex) {
            fragment.appendChild(
              document.createTextNode(text.slice(lastIndex, match.index))
            );
          }

          // Add highlighted match
          const mark = document.createElement('mark');
          mark.className = 'search-highlight bg-cyan-200 px-0.5 rounded';
          mark.textContent = match[0];
          fragment.appendChild(mark);

          lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        // Replace the text node with the fragment
        node.parentNode?.replaceChild(fragment, node);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Skip certain elements where we shouldn't highlight
      const tagName = (node as Element).tagName.toLowerCase();
      if (['script', 'style', 'mark', 'code', 'pre'].includes(tagName)) {
        return;
      }

      // Process child nodes (create array copy to avoid mutation issues)
      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child));
    }
  }

  processNode(tempDiv);
  return tempDiv.innerHTML;
}

/**
 * Highlight text for plain text content (non-HTML)
 */
function highlightPlainText(
  text: string,
  comments: CommentResponse[],
  selectedCommentId: number | null,
  highlightTerms: string[],
  englishExact = false
): React.ReactNode[] {
  const ranges = englishExact ? findTermRanges(text, highlightTerms, true) : [];
  const searchText = (start: number, end: number): React.ReactNode => {
    const out: React.ReactNode[] = [];
    let pos = start;
    ranges.forEach((r, i) => {
      const from = Math.max(start, r.start);
      const to = Math.min(end, r.end);
      if (from >= to) return;
      out.push(text.slice(pos, from), <mark key={i} className="search-highlight bg-cyan-200 px-0.5 rounded">{text.slice(from, to)}</mark>);
      pos = to;
    });
    out.push(text.slice(pos, end));
    return out;
  };
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyIndex = 0;

  // First, find all comment highlights
  const commentHighlights: { start: number; end: number; comment: CommentResponse }[] = [];

  for (const comment of comments) {
    if (!comment.context_text) continue;
    const idx = remaining.toLowerCase().indexOf(comment.context_text.toLowerCase());
    if (idx !== -1) {
      commentHighlights.push({
        start: idx,
        end: idx + comment.context_text.length,
        comment,
      });
    }
  }

  // Sort by start position
  commentHighlights.sort((a, b) => a.start - b.start);

  // Build parts with highlights
  let pos = 0;
  for (const highlight of commentHighlights) {
    if (highlight.start > pos) {
      // Add text before highlight
      parts.push(<span key={keyIndex++}>{searchText(pos, highlight.start)}</span>);
    }

    const isSelected = highlight.comment.id === selectedCommentId;
    parts.push(
      <mark
        key={keyIndex++}
        id={`comment-highlight-${highlight.comment.id}`}
        className={isSelected ? 'bg-amber-300 border-b-2 border-amber-500' : 'bg-amber-100'}
        data-comment-id={highlight.comment.id}
      >
        {remaining.slice(highlight.start, highlight.end)}
      </mark>
    );
    pos = highlight.end;
  }

  // Add remaining text
  if (pos < remaining.length) {
    parts.push(<span key={keyIndex++}>{searchText(pos, remaining.length)}</span>);
  }

  return parts.length > 0 ? parts : [<span key={0}>{text}</span>];
}

/**
 * Draft Content Renderer Component
 */
export const DraftContentRenderer: React.FC<DraftContentRendererProps> = ({
  contentHtml,
  contentText,
  comments = [],
  selectedCommentId = null,
  onCommentClick,
  highlightTerms = [],
  englishExact = false,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Debug logging for table rendering investigation
  React.useEffect(() => {
    if (contentHtml && contentHtml.includes('<table')) {
      console.log('[DraftContentRenderer] Section has table HTML:', {
        hasContentHtml: !!contentHtml,
        contentHtmlPreview: contentHtml?.substring(0, 200),
        contentTextPreview: contentText?.substring(0, 100),
      });
    }
  }, [contentHtml, contentText]);

  // Additional debug: log what processedHtml contains
  React.useEffect(() => {
    if (contentHtml && contentHtml.includes('<table')) {
      // Check if table survived DOMPurify
      const sanitized = DOMPurify.sanitize(contentHtml, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ADD_ATTR: ['data-comment-id', 'data-highlight'],
      });
      console.log('[DraftContentRenderer] After DOMPurify:', {
        inputHasTable: contentHtml.includes('<table'),
        outputHasTable: sanitized.includes('<table'),
        sanitizedPreview: sanitized.substring(0, 300),
      });
    }
  }, [contentHtml]);

  // Process HTML content with highlights
  const processedHtml = useMemo(() => {
    if (!contentHtml) return null;

    let html = contentHtml;

    // First apply comment highlighting
    if (comments.length > 0) {
      html = highlightCommentsInHtml(html, comments, selectedCommentId);
    }

    // Then apply search term highlighting
    if (highlightTerms.length > 0) {
      html = highlightSearchTermsInHtml(html, highlightTerms, englishExact);
    }

    // Sanitize final HTML
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ADD_ATTR: ['data-comment-id', 'data-highlight'],
    });
  }, [contentHtml, comments, selectedCommentId, highlightTerms, englishExact]);

  // Handle click events on comment highlights
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onCommentClick) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const commentMark = target.closest('mark[data-comment-id]') as HTMLElement;

      if (commentMark) {
        const commentId = commentMark.dataset['commentId'];
        if (commentId) {
          onCommentClick(parseInt(commentId, 10));
        }
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [onCommentClick, processedHtml]);

  // Render HTML content if available
  if (processedHtml) {
    return (
      <div
        ref={containerRef}
        className={`draft-content prose prose-slate prose-sm max-w-none ${className}`}
        dangerouslySetInnerHTML={{ __html: processedHtml }}
      />
    );
  }

  // Fallback to plain text with highlighting
  const plainTextContent = highlightPlainText(
    contentText,
    comments,
    selectedCommentId,
    highlightTerms,
    englishExact
  );

  return (
    <div
      ref={containerRef}
      className={`whitespace-pre-wrap text-slate-700 ${className}`}
      onClick={(e) => {
        if (!onCommentClick) return;
        const target = e.target as HTMLElement;
        const commentMark = target.closest('mark[data-comment-id]') as HTMLElement;
        if (commentMark) {
          const commentId = commentMark.dataset['commentId'];
          if (commentId) {
            onCommentClick(parseInt(commentId, 10));
          }
        }
      }}
    >
      {plainTextContent}
    </div>
  );
};

export default DraftContentRenderer;
